import React, { useEffect, useRef } from 'react';
import './AuthButton.css';

interface AuthButtonProps {
  user: { name: string; avatar_url: string | null } | null;
  isLoading: boolean;
  onSignIn: (idToken: string) => Promise<void>;
  onSignOut: () => void;
}

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: any) => void;
          renderButton: (element: HTMLElement, config: any) => void;
          prompt: () => void;
        };
      };
    };
  }
}

const AuthButton: React.FC<AuthButtonProps> = ({ user, isLoading, onSignIn, onSignOut }) => {
  const googleButtonRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Wait for Google script to load, then initialize
    const initializeGoogleSignIn = () => {
      if (!user && !isLoading && window.google && googleButtonRef.current) {
        try {
          const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID || '';
          console.log('🔍 Initializing Google Sign-In with Client ID:', clientId);

          window.google.accounts.id.initialize({
            client_id: clientId,
            callback: async (response: any) => {
              try {
                console.log('✅ Google Sign-In successful, credential received');
                await onSignIn(response.credential);
              } catch (error) {
                console.error('Sign in failed:', error);
                alert('Sign in failed. Please try again.');
              }
            }
          });

          window.google.accounts.id.renderButton(googleButtonRef.current, {
            theme: 'outline',
            size: 'large',
            text: 'signin_with',
            shape: 'rectangular'
          });

          console.log('✅ Google Sign-In button rendered');
        } catch (error) {
          console.error('Failed to initialize Google Sign In:', error);
        }
      }
    };

    // If Google is already loaded, initialize immediately
    if (window.google) {
      initializeGoogleSignIn();
    } else {
      // Otherwise, wait for the script to load
      const checkGoogleLoaded = setInterval(() => {
        if (window.google) {
          clearInterval(checkGoogleLoaded);
          initializeGoogleSignIn();
        }
      }, 100);

      return () => clearInterval(checkGoogleLoaded);
    }
  }, [user, isLoading, onSignIn]);

  if (isLoading) {
    return (
      <div className="auth-button loading">
        <span>Loading...</span>
      </div>
    );
  }

  if (user) {
    return (
      <div className="auth-button authenticated">
        <div className="user-info">
          {user.avatar_url ? (
            <img src={user.avatar_url} alt={user.name} className="user-avatar" />
          ) : (
            <div className="user-avatar-placeholder">{user.name.charAt(0)}</div>
          )}
          <span className="user-name">{user.name}</span>
        </div>
        <button className="sign-out-button" onClick={onSignOut}>
          Sign Out
        </button>
      </div>
    );
  }

  return (
    <div className="auth-button">
      <div ref={googleButtonRef} className="google-signin-button"></div>
    </div>
  );
};

export default AuthButton;
